import {
  CardCatalogSchema,
  DeckDefinitionSchema,
  createEngineGameplayCatalogV3,
  expandDeckDefinition,
  validateDeck,
  type CardCatalog,
  type DeckDefinition,
  type EngineResourcePool,
  type ResourceCost,
} from "@fate-kingdom/card-data";

import {
  COMMAND_TEST_CARD_CATALOG,
  TEST_COMMAND_ARMY_DEFINITION_IDS,
  TEST_COMMAND_CONSPIRACY_DEFINITION_ID,
} from "./command-card-fixtures.js";
import { TEST_LAND_DEFINITION_IDS } from "./setup-card-fixtures.js";

export const COMMANDER_DEPLOYMENT_TEST_CARD_DATA_VERSION = "0.5.0-commander-deployment.1";

export const TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS = {
  balanced: "card.test-deployment-commander-balanced",
  highCapacity: "card.test-deployment-commander-high-capacity",
  lowCapacity: "card.test-deployment-commander-low-capacity",
  defensive: "card.test-deployment-commander-defensive",
} as const;

const DEPLOYMENT_MANDATE_KEYS = ["balanced", "high-capacity", "low-capacity", "defensive"] as const;

function mandateConditionId(key: (typeof DEPLOYMENT_MANDATE_KEYS)[number]): string {
  return `condition.test-deployment-commander-${key}`;
}

function mandateEffectId(key: (typeof DEPLOYMENT_MANDATE_KEYS)[number]): string {
  return `effect.test-deployment-commander-${key}`;
}

function makeDeploymentCommander(
  id: string,
  name: string,
  key: (typeof DEPLOYMENT_MANDATE_KEYS)[number],
  cost: ResourceCost,
  stats: { readonly attack: number; readonly guard: number; readonly armyCapacity: number },
) {
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id,
    type: "commander",
    name,
    cost,
    rulesText:
      "TEST ONLY: printed Commander values for deterministic deployment and replacement scenarios.",
    stats,
    mandates: [
      {
        mandateId: `mandate.test-deployment-commander-${key}`,
        name: `${name} inert Mandate`,
        condition: { conditionId: mandateConditionId(key) },
        effects: [{ effectId: mandateEffectId(key) }],
      },
    ],
  };
}

/**
 * Isolated test-only content for Commander deployment/replacement. It extends
 * the Command-disband catalog without mutating any historical fixture.
 */
export const COMMANDER_DEPLOYMENT_TEST_CARD_CATALOG: CardCatalog = CardCatalogSchema.parse({
  ...COMMAND_TEST_CARD_CATALOG,
  cardDataVersion: COMMANDER_DEPLOYMENT_TEST_CARD_DATA_VERSION,
  cards: [
    ...COMMAND_TEST_CARD_CATALOG.cards,
    makeDeploymentCommander(
      TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.balanced,
      "TEST ONLY Balanced Commander",
      "balanced",
      { food: 1, trade: 1 },
      { attack: 2, guard: 2, armyCapacity: 3 },
    ),
    makeDeploymentCommander(
      TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.highCapacity,
      "TEST ONLY High-Capacity Commander",
      "high-capacity",
      { production: 3 },
      { attack: 1, guard: 2, armyCapacity: 5 },
    ),
    makeDeploymentCommander(
      TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.lowCapacity,
      "TEST ONLY Low-Capacity Commander",
      "low-capacity",
      { science: 1 },
      { attack: 3, guard: 1, armyCapacity: 1 },
    ),
    makeDeploymentCommander(
      TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.defensive,
      "TEST ONLY Defensive Commander",
      "defensive",
      { culture: 2 },
      { attack: 1, guard: 4, armyCapacity: 2 },
    ),
  ],
  conditions: [
    ...COMMAND_TEST_CARD_CATALOG.conditions,
    ...DEPLOYMENT_MANDATE_KEYS.map((key) => ({
      schemaVersion: 1,
      definitionVersion: 1,
      id: mandateConditionId(key),
      rulesText: `TEST ONLY: inert ${key} Commander Mandate condition; not evaluated.`,
    })),
  ],
  effects: [
    ...COMMAND_TEST_CARD_CATALOG.effects,
    ...DEPLOYMENT_MANDATE_KEYS.map((key) => ({
      schemaVersion: 1,
      definitionVersion: 1,
      id: mandateEffectId(key),
      rulesText: `TEST ONLY: inert ${key} Commander Mandate effect; not evaluated.`,
    })),
  ],
});

export const COMMANDER_DEPLOYMENT_TEST_ENGINE_CATALOG = createEngineGameplayCatalogV3(
  COMMANDER_DEPLOYMENT_TEST_CARD_CATALOG,
);

/** Resource cases used to prove exact payment and that Trade is not a wildcard. */
export const COMMANDER_DEPLOYMENT_TEST_RESOURCE_POOLS = {
  balancedExact: {
    food: 1,
    production: 0,
    trade: 1,
    science: 0,
    culture: 0,
  },
  lowCapacityExact: {
    food: 0,
    production: 0,
    trade: 0,
    science: 1,
    culture: 0,
  },
  lowCapacityTradeSubstitutionAttempt: {
    food: 0,
    production: 0,
    trade: 3,
    science: 0,
    culture: 0,
  },
} as const satisfies Record<string, EngineResourcePool>;

const deploymentDeckInput = DeckDefinitionSchema.parse({
  schemaVersion: 1,
  definitionVersion: 1,
  cardDataVersion: COMMANDER_DEPLOYMENT_TEST_CARD_DATA_VERSION,
  id: "deck.test-commander-deployment",
  name: "TEST ONLY: Commander deployment and replacement deck",
  cards: [
    { cardId: TEST_LAND_DEFINITION_IDS.plains, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.mountain, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.river, count: 3 },
    { cardId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.balanced, count: 2 },
    { cardId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.highCapacity, count: 2 },
    { cardId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.lowCapacity, count: 2 },
    { cardId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.defensive, count: 2 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.vanguard, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.sentries, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.outriders, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.wardens, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.scouts, count: 3 },
    { cardId: "card.test-filler-army-01", count: 2 },
    { cardId: "card.test-filler-army-02", count: 2 },
    { cardId: "card.test-filler-army-03", count: 2 },
    { cardId: "card.test-filler-army-04", count: 1 },
    { cardId: TEST_COMMAND_CONSPIRACY_DEFINITION_ID, count: 1 },
  ],
});

const validatedDeploymentDeck = validateDeck(
  deploymentDeckInput,
  COMMANDER_DEPLOYMENT_TEST_CARD_CATALOG,
);
if (!validatedDeploymentDeck.success) {
  throw new Error(
    `Invalid test-only Commander deployment deck: ${validatedDeploymentDeck.error.message}`,
  );
}

export const COMMANDER_DEPLOYMENT_TEST_DECK: DeckDefinition = validatedDeploymentDeck.data;
export const COMMANDER_DEPLOYMENT_TEST_DECK_CARD_IDS = expandDeckDefinition(
  COMMANDER_DEPLOYMENT_TEST_DECK,
);
